// ApiClientsProvider.tsx

import React, { createContext, useContext, useMemo } from 'react';
import { createApiClient, ApiClient,  useImpersonation } from '@center-technologies/shared-api-clients';
import type { AxiosResponse, AxiosError } from 'axios';

export interface ApiClients {
  graphClient: ApiClient;
  schemaClient: ApiClient;
  permissionClient: ApiClient;
  organizationClient: ApiClient;
  notificationClient: ApiClient;
  searchClient: ApiClient;
  /** The SAME token source the clients' interceptor uses (impersonation/org-session token when
   *  present, undefined otherwise — callers fall back to the Descope session token exactly like
   *  the interceptor). Hand this to anything that talks to services outside these clients (e.g.
   *  the composer agent SSE transport): a bare session token lacks the org claim and 401s
   *  org-scoped routes. */
  getAuthToken?: () => Promise<string | undefined>;
}

export interface ApiClientsProviderProps {
  children?: React.ReactNode;
  /** Optional observers invoked for every API response across all clients.
   *  Used by the PWA to wire analytics; design-system stays decoupled. */
  onResponse?: (response: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
  /**
   * The host app's auth-readiness signal (Center-PWA supplies Descope's session state).
   * `accountKey` should change only when the signed-in identity changes, so clients aren't
   * rebuilt on incidental re-renders.
   */
  auth: {
    isAuthenticated: boolean;
    accountKey: string;
    /**
     * Renews the signed-in session when a request is answered 401 (Center-PWA: Descope's
     * `refresh`). Resolves false when the session has ended — the host's own session watcher,
     * not the clients, then signs the user out. See `addTokenRefreshInterceptor`.
     */
    refreshSession?: () => Promise<boolean>;
  };
}

export const ApiClientsContext = createContext<ApiClients | null>(null);

export const ApiClientsProvider: React.FC<ApiClientsProviderProps> = ({ children, onResponse, onError, auth }) => {
  const { getEffectiveToken } = useImpersonation();

  // The bearer token is resolved per-request through `tokenProvider`, so the clients are
  // only dropped on a real sign-out.
  const ready = auth.isAuthenticated;
  const accountKey = auth.accountKey;
  const refreshSession = auth.refreshSession;

  const clients = useMemo<ApiClients | null>(() => {
    if (!ready) {
      return null;
    }

    // Create a token provider that uses impersonation when available...
    const tokenProvider = () => getEffectiveToken();
    const baseConfig = { tokenProvider, refreshSession, onResponse, onError };

    return {
      graphClient: createApiClient(
        process.env.REACT_APP_GRAPH_ENDPOINT || 'http://localhost:3001',
        baseConfig
      ),
      schemaClient: createApiClient(
        process.env.REACT_APP_SCHEMA_ENDPOINT || 'http://localhost:3002',
        baseConfig
      ),
      permissionClient: createApiClient(
        process.env.REACT_APP_PERMISSIONS_ENDPOINT || 'http://localhost:3004',
        baseConfig
      ),
      organizationClient: createApiClient(
        process.env.REACT_APP_ORGANIZATION_ENDPOINT || 'http://localhost:3003',
        baseConfig
      ),
      notificationClient: createApiClient(
        process.env.REACT_APP_NOTIFICATION_ENDPOINT || 'http://localhost:3006',
        baseConfig
      ),
      searchClient: createApiClient(
        process.env.REACT_APP_SEARCH_ENDPOINT || 'http://localhost:3007',
        baseConfig
      ),
      getAuthToken: tokenProvider,
    };
    // `accountKey`, not the session object: depend on who is signed in, not on the identity of
    // the claims object the SDK hands us this render.
  }, [ready, accountKey, getEffectiveToken, refreshSession, onResponse, onError]);

  return (
    <ApiClientsContext.Provider value={clients}>
      {children}
    </ApiClientsContext.Provider>
  );
};

// Export the safe version that returns null when not ready
export function useApiClientsOrNull(): ApiClients | null {
  return useContext(ApiClientsContext);
}

// Keep the original for backward compatibility, but make it safer
export function useApiClients(): ApiClients {
  const ctx = useContext(ApiClientsContext);
  if (!ctx) {
    throw new Error('useApiClients: API clients not ready. Ensure the user is authenticated and the session has finished loading.');
  }
  return ctx;
}

// NEW: Add a hook to check if clients are ready
export function useAreApiClientsReady(): boolean {
  const ctx = useContext(ApiClientsContext);
  return ctx !== null;
}